import useSWR from 'swr';
import _ from 'underscore';
import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import edm from '@bingads-webui-component-legacy/edm-campaign/index';
import { availableGranularities, fetchAggregatedData } from '@bingads-webui-campaign/overview-cards-common';
import dateUtil from '@bingads-webui-component-legacy/humanize/date';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import config from '@bingads-webui-component-legacy/config/index';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { cardDeviceId } from '@bingads-webui-campaign/fluent-page-consts';
import { getNavigationLevel, NavigationLevel } from '@bingads-webui-campaign/fluent-page-data';
import { UseDataProps } from '../utils/types';

export function useData({
  dateRange,
  filter,
  campaignType,
  getCardsConfig,
  willFetchData,
  willRender,
  done,
}: UseDataProps) {
  const { scope } = useContext(ScopeContext) as any;
  const { campaignId } = scope.values;
  const aid = _.get(scope, ['values', 'aid']);
  const isAccountIdAvailable = !!aid;

  const navigationLevel = useMemo(() => getNavigationLevel(scope.values), [scope.values]);
  const granularity = useMemo(() => availableGranularities(dateRange, dateUtil)[0], [dateRange]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const extendedFilter = useMemo(() => {
    if (campaignId) {
      return [...filter, {
        filter: {
          key: 'CampaignId',
          value: {
            query: {
              Id: Number(campaignId),
            },
          },
        },
      }];
    }
    return filter;
  }, [campaignId, filter]);

  const fetchSingleTile = navigationLevel === NavigationLevel.Campaign;

  const entitySet = useMemo(() => {
    if (campaignId) {
      return edm.currentCampaign.AdGroups;
    }
    if (isAccountIdAvailable) {
      return edm.currentAccount.Campaigns;
    }
    return edm.currentCustomer.Accounts;
  }, [campaignId, isAccountIdAvailable]);

  const fetcher = useCallback(async () => {
    setIsLoading(true);
    const data = await fetchAggregatedData({
      entitySet,
      dateRange,
      filter: extendedFilter,
      campaignType,
      jsdata,
      dateUtil,
      navigationLevel,
      getCardsConfig,
      fetchSingleTile,
      isDevice: true,
      permissions: config.get('Permissions'),
      dfmCacheKey: cardDeviceId,
    });

    if (!data || (!data.raw && !data.OverallTotals)) {
      throw new Error('Error loading data for device card');
    }

    return data;
  }, [entitySet, dateRange, extendedFilter, getCardsConfig, navigationLevel, fetchSingleTile, campaignType]);

  const { data, error } = useSWR(
    [dateRange, extendedFilter, getCardsConfig, navigationLevel, fetchSingleTile],
    fetcher,
    {
      onSuccess: () => {
        setIsLoading(false);
        willRender();
        done();
      },
      onError: () => {
        setIsLoading(false);
        setHasError(true);
      },
    }
  );

  useEffect(() => {
    if (dateRange && filter) {
      willFetchData();
    }
  }, [dateRange, filter, getCardsConfig, granularity, navigationLevel, willFetchData]);

  const deviceData = useMemo(() => {
    if (campaignId) {
      return data?.raw?.['@ns.unfiltered.totals'] || {};
    }
    return data || {};
  }, [data, campaignId]);

  return {
    aggregatedData: deviceData || null,
    isLoading,
    hasError: !!error,
  };
}
